import { useEffect, useRef, useState } from "react";
import { styled, useTheme } from "@mui/material/styles";
import {
  CardMedia,
  Button,
  Typography,
  Box,
  Skeleton,
  Fade,
  useMediaQuery,
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import {
  PRODUCT_IMG as PRODUCT_IMG_CFG,
  productImageUrl,
  productImageSrcSet,
} from "../../../utils/productImages";
import { mediaUrl } from "../../../utils/cfImage";

// Lean ladder: 800 (mobile) / 1200 (desktop)
const PRODUCT_IMG = PRODUCT_IMG_CFG.list;

const SLIDE_DURATION_MS = 450;
const SLIDE_EASING = "cubic-bezier(0.22, 1, 0.36, 1)";
const SWIPE_THRESHOLD_PX = 40;
const NAV_ARROW_SLIDE_PX = 8;
const NAV_ARROW_TRANSITION =
  "opacity 0.28s ease, transform 0.35s cubic-bezier(0.22, 1, 0.36, 1)";

const navArrowBtnSx = {
  width: 28,
  height: 28,
  minWidth: 28,
  borderRadius: "50%",
  bgcolor: "#fff",
  boxShadow: "0 1px 4px rgba(0,0,0,0.14)",
  color: "#000",
  top: "50%",
  fontSize: "13px",
  lineHeight: 1,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  "&:hover, &:focus": {
    bgcolor: "#fff",
  },
  "&:disabled": {
    display: "none !important",
  },
};

const imageFrameSx = {
  position: "relative",
  width: "100%",
  aspectRatio: "4 / 5",
  overflow: "hidden",
  bgcolor: "#f4f4f1",
  display: "block",
  touchAction: "pan-y",
  cursor: "pointer",
};

const ImageBtn = styled(Button)({
  position: "absolute",
  background: "transparent",
  zIndex: "1",
  padding: 0,
  minWidth: 0,
  color: "#000",
  textTransform: "none",
  fontWeight: 400,
  "&:disabled": {
    pointerEvents: "auto",
    opacity: 0.25,
  },
  "&:hover, &:focus": {
    background: "transparent",
  },
});

export default function ProductSliderView(props) {
  const { component } = props;
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));

  const [paperHovered, setHovered] = useState(false);
  const [loadedImages, setLoadedImages] = useState({});
  const [activeImage, setActiveImage] = useState(0);
  const [showModel, setShowModel] = useState(false);
  const [slideTransition, setSlideTransition] = useState(true);
  const [dragOffset, setDragOffset] = useState(0);

  const swipeGuardRef = useRef(false);
  const dragOffsetRef = useRef(0);
  const dragRef = useRef({
    startX: 0,
    startY: 0,
    tracking: false,
    horizontal: false,
    pointerId: null,
    captured: false,
  });

  const devHelper = component.getHelpers("dev");
  const filesUrl = component.getHelpers("env").get("files_url");
  const imgVersion = component.getHelpers("value").getValue("images-version");

  const product = component.getData("default.product", null);
  const images = component.getData("default.images", []);

  let model_img = null;
  if (images.length) {
    const modelIndex = images.findIndex((img) =>
      img.name.includes("model".toLowerCase()),
    );
    if (modelIndex !== -1) {
      model_img = images[modelIndex].name;
    } else if (images[1] !== undefined) {
      model_img = images[1].name;
    }
  }

  const gems = devHelper.getObjectValue(product, "_values.gems", []);
  const slug = devHelper.getObjectValue(product, "_values.slug");
  const wishlist = component.getData("default.wishlist", false);
  const collections = devHelper.getObjectValue(
    product,
    "_values.collections",
    [],
  );
  const genders = devHelper.getObjectValue(product, "_values.genders", []);

  const {
    showName = true,
    showCollection = true,
    showPrice = true,
    showGems = true,
    showCuration = true,
    enableImageSwipe = true,
  } = component.props.productDisplay || {};

  const collection = collections[0] !== undefined ? collections[0] : null;
  const gender = genders[0] !== undefined ? genders[0] : null;

  const updateWishList = (boolean) => {
    boolean
      ? component.addToWishList(slug)
      : component.removeFromWishList(slug);

    component.setData("default.wishlist", true);
  };

  const changeProduct = (gem, number) => {
    goToSlide(0, { animate: false });
    setShowModel(false);

    product.active_gem = number;
    const has_size = gem.item._values.sizes.length;
    const values = has_size
      ? gem.item._values.sizes[0]
      : gem.item._values.default;

    component.setProductValues(product, values, has_size);
  };

  const goToSlide = (index, { animate = true } = {}) => {
    if (!images.length) return;

    const nextIndex = Math.max(0, Math.min(index, images.length - 1));
    setSlideTransition(animate);
    setActiveImage(nextIndex);
    setDragOffset(0);

    if (!animate) {
      requestAnimationFrame(() => {
        setSlideTransition(true);
      });
    }
  };

  const handleNext = () => {
    goToSlide(activeImage + 1);
  };

  const handleBack = () => {
    goToSlide(activeImage - 1);
  };

  const productName = devHelper.getObjectValue(product, "_values.name");
  const productSku = devHelper.getObjectValue(product, "_values.sku");
  const productUrl = `/product/${slug}/${productSku}`;
  const hasMultipleImages = images.length > 1;
  const imageCount = Math.max(images.length, 1);
  const showNavArrows = isDesktop && paperHovered;

  const getImagePath = (index) => {
    if (!images.length) {
      return "assets/product-not-found.jpg";
    }

    if (component.getData(`default.img-error-product-${index}`, false)) {
      return "assets/product-not-found.jpg";
    }

    const imageName = devHelper.getObjectValue(images[index], "name");
    return imageName
      ? `products/${imageName}`
      : "assets/product-not-found.jpg";
  };

  const getProductImageUrl = (index) =>
    productImageUrl(
      filesUrl,
      getImagePath(index),
      imgVersion,
      PRODUCT_IMG.default,
    );

  const getProductImageSrcSet = (index) =>
    productImageSrcSet(
      filesUrl,
      getImagePath(index),
      imgVersion,
      PRODUCT_IMG.srcSet,
    );

  const modelImagePath = model_img ? `products/${model_img}` : null;
  const modelImageUrl = modelImagePath
    ? productImageUrl(
        filesUrl,
        modelImagePath,
        imgVersion,
        PRODUCT_IMG.default,
      )
    : null;
  const modelImageSrcSet = modelImagePath
    ? productImageSrcSet(
        filesUrl,
        modelImagePath,
        imgVersion,
        PRODUCT_IMG.srcSet,
      )
    : null;

  const markImageLoaded = (index) => {
    setLoadedImages((prev) =>
      prev[index] ? prev : { ...prev, [index]: true },
    );
  };

  const handleImageError = (index) => {
    component.setData({
      [`default.img-error-product-${index}`]: true,
    });
    markImageLoaded(index);
  };

  useEffect(() => {
    setShowModel(false);
  }, [activeImage, slug]);

  useEffect(() => {
    if (!modelImageUrl) return undefined;

    const preload = new Image();
    preload.src = modelImageUrl;
    return undefined;
  }, [modelImageUrl]);

  useEffect(() => {
    if (!images.length) return undefined;

    [activeImage + 1, activeImage - 1].forEach((index) => {
      if (index < 0 || index >= images.length) return;

      const imageName = devHelper.getObjectValue(images[index], "name");
      if (!imageName) return;

      const preload = new Image();
      preload.src = productImageUrl(
        filesUrl,
        `products/${imageName}`,
        imgVersion,
        PRODUCT_IMG.default,
      );
    });
  }, [activeImage, images, filesUrl, imgVersion, devHelper]);

  const showImageMeta = loadedImages[activeImage] || !images.length;
  const showActiveSkeleton = images.length > 0 && !loadedImages[activeImage];

  const handleImageFrameEnter = () => {
    if (model_img) {
      setShowModel(true);
    }
  };

  const handleImageFrameLeave = () => {
    setShowModel(false);
  };

  const handleCardLeave = () => {
    setHovered(false);
    setShowModel(false);
    goToSlide(0, { animate: false });
  };

  const handleFrameClick = (event) => {
    if (swipeGuardRef.current) {
      swipeGuardRef.current = false;
      event.preventDefault();
    }
  };

  const applyDragOffset = (deltaX) => {
    const maxOffset =
      (activeImage === 0 && deltaX > 0) ||
      (activeImage === images.length - 1 && deltaX < 0)
        ? deltaX * 0.35
        : deltaX;

    setDragOffset(maxOffset);
    dragOffsetRef.current = maxOffset;
  };

  const finishDrag = () => {
    if (!dragRef.current.tracking || !hasMultipleImages) return;

    const { horizontal } = dragRef.current;
    const offset = dragOffsetRef.current;
    dragRef.current.tracking = false;
    dragRef.current.pointerId = null;
    dragRef.current.captured = false;
    setSlideTransition(true);

    if (horizontal) {
      if (offset <= -SWIPE_THRESHOLD_PX) {
        handleNext();
      } else if (offset >= SWIPE_THRESHOLD_PX) {
        handleBack();
      } else {
        setDragOffset(0);
        dragOffsetRef.current = 0;
      }
    } else {
      setDragOffset(0);
      dragOffsetRef.current = 0;
    }
  };

  const handlePointerDown = (event) => {
    if (!enableImageSwipe || !hasMultipleImages) return;
    if (event.pointerType === "mouse" && event.button !== 0) return;
    if (event.target.closest("button")) return;

    dragRef.current = {
      startX: event.clientX,
      startY: event.clientY,
      tracking: true,
      horizontal: false,
      pointerId: event.pointerId,
      captured: false,
    };
    setSlideTransition(false);
    setDragOffset(0);
    dragOffsetRef.current = 0;
    swipeGuardRef.current = false;
  };

  const handlePointerMove = (event) => {
    if (
      !dragRef.current.tracking ||
      !enableImageSwipe ||
      !hasMultipleImages ||
      event.pointerId !== dragRef.current.pointerId
    ) {
      return;
    }

    const deltaX = event.clientX - dragRef.current.startX;
    const deltaY = event.clientY - dragRef.current.startY;

    if (!dragRef.current.horizontal) {
      if (Math.abs(deltaX) < 8 && Math.abs(deltaY) < 8) {
        return;
      }

      if (Math.abs(deltaX) <= Math.abs(deltaY)) {
        dragRef.current.tracking = false;
        dragRef.current.pointerId = null;
        setDragOffset(0);
        dragOffsetRef.current = 0;
        setSlideTransition(true);
        return;
      }

      dragRef.current.horizontal = true;
      if (!dragRef.current.captured) {
        event.currentTarget.setPointerCapture(event.pointerId);
        dragRef.current.captured = true;
      }
    }

    swipeGuardRef.current = true;
    event.preventDefault();
    applyDragOffset(deltaX);
  };

  const handlePointerUp = (event) => {
    if (event.pointerId !== dragRef.current.pointerId) return;

    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }

    finishDrag();
  };

  const slideTransform = hasMultipleImages
    ? `translateX(calc(-${(activeImage * 100) / imageCount}% + ${dragOffset}px))`
    : "translateX(0)";

  return (
    <Box onMouseEnter={() => setHovered(true)} onMouseLeave={handleCardLeave}>
      {product && (
        <Box>
          <Box
            component={RouterLink}
            to={productUrl}
            onMouseEnter={handleImageFrameEnter}
            onMouseLeave={handleImageFrameLeave}
            onClick={handleFrameClick}
            onPointerDown={enableImageSwipe ? handlePointerDown : undefined}
            onPointerMove={enableImageSwipe ? handlePointerMove : undefined}
            onPointerUp={enableImageSwipe ? handlePointerUp : undefined}
            onPointerCancel={enableImageSwipe ? handlePointerUp : undefined}
            sx={{
              ...imageFrameSx,
              touchAction: enableImageSwipe ? "pan-y" : "pan-x pan-y",
              textDecoration: "none",
              color: "inherit",
            }}
          >
            <Fade in={showActiveSkeleton} timeout={250}>
              <Skeleton
                variant="rectangular"
                sx={{
                  position: "absolute",
                  inset: 0,
                  width: "100%",
                  height: "100%",
                  borderRadius: 0,
                  zIndex: 0,
                  pointerEvents: "none",
                }}
              />
            </Fade>

            <Box
              sx={{
                position: "absolute",
                inset: 0,
                zIndex: 1,
                overflow: "hidden",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  height: "100%",
                  width: `${imageCount * 100}%`,
                  transform: slideTransform,
                  transition: slideTransition
                    ? `transform ${SLIDE_DURATION_MS}ms ${SLIDE_EASING}`
                    : "none",
                  willChange: "transform",
                }}
              >
                {(images.length ? images : [null]).map((image, index) => (
                  <Box
                    key={`${slug}-slide-${index}`}
                    sx={{
                      width: `${100 / imageCount}%`,
                      flexShrink: 0,
                      height: "100%",
                    }}
                  >
                    <Box
                      component="img"
                      src={getProductImageUrl(index)}
                      srcSet={getProductImageSrcSet(index)}
                      sizes={PRODUCT_IMG.sizes}
                      alt={`${productName} image ${index + 1}`}
                      loading={index === 0 ? "eager" : "lazy"}
                      decoding="async"
                      draggable={false}
                      onLoad={() => markImageLoaded(index)}
                      onError={() => handleImageError(index)}
                      sx={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        display: "block",
                        opacity: loadedImages[index] ? 1 : 0,
                        transition: "opacity 0.3s ease",
                        pointerEvents: "none",
                        userSelect: "none",
                      }}
                    />
                  </Box>
                ))}
              </Box>
            </Box>

            {modelImageUrl && (
              <Box
                component="img"
                src={modelImageUrl}
                srcSet={modelImageSrcSet}
                sizes={PRODUCT_IMG.sizes}
                alt={`${productName} model`}
                decoding="async"
                draggable={false}
                sx={{
                  position: "absolute",
                  inset: 0,
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  display: "block",
                  opacity: showModel && loadedImages[activeImage] ? 1 : 0,
                  transition: "none",
                  pointerEvents: "none",
                  zIndex: 2,
                  userSelect: "none",
                }}
              />
            )}
            {showCuration && devHelper.getObjectValue(product, "curation") && (
              <Fade in={showImageMeta} timeout={300}>
                <Typography
                  disabled
                  sx={{
                    position: "absolute",
                    top: 10,
                    left: 10,
                    fontSize: { xs: 12, sm: 14 },
                    bgcolor: "#fff",
                    zIndex: 4,
                    p: 1,
                    lineHeight: 1,
                    borderRadius: 0,
                    color: "#000",
                    pointerEvents: "none",
                  }}
                >
                  {devHelper.getObjectValue(
                    product,
                    "curation.item._values.name",
                  )}
                </Typography>
              </Fade>
            )}

            <Box
              sx={{
                position: "absolute",
                inset: 0,
                zIndex: 3,
                pointerEvents: "none",
              }}
            >
              <ImageBtn
                disableRipple
                id="heart"
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  updateWishList(!wishlist);
                }}
                sx={{
                  top: 10,
                  right: 10,
                  fontSize: { xs: 14, sm: 16 },
                  width: 28,
                  height: 28,
                  minWidth: 28,
                  borderRadius: "50%",
                  // bgcolor: "#fff",
                  // boxShadow: "0 1px 4px rgba(0,0,0,0.14)",
                  color: "#000",
                  lineHeight: 1,
                  pointerEvents: "auto",
                  display: {
                    xs: "block",
                    md: paperHovered ? "block" : "none",
                  },
                }}
                aria-label="Add to Wishlist"
              >
                <i
                  className={
                    wishlist ? "fa-solid fa-heart" : "fa-light fa-heart"
                  }
                />
              </ImageBtn>

              {hasMultipleImages && (
                <>
                  <ImageBtn
                    id="left-arrow"
                    onClick={(event) => {
                      event.preventDefault();
                      event.stopPropagation();
                      handleBack();
                    }}
                    sx={{
                      ...navArrowBtnSx,
                      left: { xs: 10, sm: 12 },
                      pointerEvents: showNavArrows ? "auto" : "none",
                      opacity: showNavArrows ? 1 : 0,
                      transform: showNavArrows
                        ? "translateY(-50%) translateX(0)"
                        : `translateY(-50%) translateX(-${NAV_ARROW_SLIDE_PX}px)`,
                      transition: NAV_ARROW_TRANSITION,
                      "@media (prefers-reduced-motion: reduce)": {
                        transition: "opacity 0.2s ease",
                        transform: "translateY(-50%)",
                      },
                    }}
                    disabled={activeImage === 0}
                    disableRipple
                    aria-label="Previous image"
                    aria-hidden={!showNavArrows}
                  >
                    <i className="fa-light fa-fw fa-arrow-left" />
                  </ImageBtn>

                  <ImageBtn
                    id="right-arrow"
                    onClick={(event) => {
                      event.preventDefault();
                      event.stopPropagation();
                      handleNext();
                    }}
                    sx={{
                      ...navArrowBtnSx,
                      right: { xs: 10, sm: 12 },
                      pointerEvents: showNavArrows ? "auto" : "none",
                      opacity: showNavArrows ? 1 : 0,
                      transform: showNavArrows
                        ? "translateY(-50%) translateX(0)"
                        : `translateY(-50%) translateX(${NAV_ARROW_SLIDE_PX}px)`,
                      transition: NAV_ARROW_TRANSITION,
                      "@media (prefers-reduced-motion: reduce)": {
                        transition: "opacity 0.2s ease",
                        transform: "translateY(-50%)",
                      },
                    }}
                    disabled={activeImage === images.length - 1}
                    disableRipple
                    aria-label="Next image"
                    aria-hidden={!showNavArrows}
                  >
                    <i className="fa-light fa-fw fa-arrow-right" />
                  </ImageBtn>
                </>
              )}
              {hasMultipleImages && (
                <Box
                  sx={{
                    position: "absolute",
                    left: "50%",
                    bottom: { xs: 4, md: 8 },
                    transform: "translateX(-50%)",
                    width: "50%",
                    display: "flex",
                    alignItems: "center",
                    gap: { xs: 0.75, md: 1 },
                    pointerEvents: "auto",
                  }}
                >
                  {images.map((image, index) => (
                    <Box
                      key={`${slug}-indicator-${image?.name || index}`}
                      component="button"
                      type="button"
                      aria-label={`Show image ${index + 1}`}
                      aria-current={activeImage === index}
                      onClick={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                        goToSlide(index);
                      }}
                      sx={{
                        flex: 1,
                        py: 1,
                        px: 0,
                        cursor: "pointer",
                        border: "none",
                        bgcolor: "transparent",
                      }}
                    >
                      <Box
                        sx={{
                          width: "100%",
                          height: "1px",
                          bgcolor: activeImage === index ? "#000" : "#b6b7ae",
                          transition: "background-color 0.25s ease",
                          borderRadius: 0,
                        }}
                      />
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
          </Box>

          <Fade in={showImageMeta} timeout={500}>
            <Box sx={{ textAlign: "center" }}>
              {showCollection && collection && (
                <Button
                  sx={{
                    fontFamily: "Spartan-SemiBold",
                    fontSize: { xs: 11, md: 12 },
                    padding: 0,
                    "&:hover, &:focus": {
                      background: "transparent",
                    },
                  }}
                  fullWidth
                  component={RouterLink}
                  to={
                    gender
                      ? "/" +
                        devHelper.getObjectValue(gender, "item._values.slug") +
                        "/collection/" +
                        devHelper.getObjectValue(
                          collection,
                          "item._values.slug",
                        )
                      : "/"
                  }
                  disableRipple
                >
                  {devHelper.getObjectValue(collection, "item._values.name")}
                </Button>
              )}
              {showName && (
                <Button
                  sx={{
                    fontFamily: "Poppins-Regular",
                    fontSize: { xs: 14, md: 17 },
                    textTransform: "none",
                    padding: "0px",
                    mt: { xs: "-5px", sm: "-12px" },
                    lineHeight: { xs: "17px", sm: "unset" },
                    "&:hover, &:focus": {
                      background: "transparent",
                      color: "#000",
                    },
                  }}
                  fullWidth
                  component={RouterLink}
                  to={productUrl}
                >
                  {devHelper.getObjectValue(product, "_values.name")}
                </Button>
              )}
              {showPrice && (
                <Typography
                  sx={{
                    fontFamily: "Spartan-SemiBold",
                    fontSize: { xs: 11, md: 12 },
                    mt: { xs: "0px", sm: "-3px" },
                  }}
                >
                  {devHelper.getObjectValue(product, "active_gem") === -1 &&
                  devHelper.getObjectValue(product, "maxPrice") !==
                    devHelper.getObjectValue(product, "_values.price")
                    ? `${component
                        .getApp()
                        .priceFormater(
                          devHelper.getObjectValue(product, "_values.price"),
                        )}-${component
                        .getApp()
                        .priceFormater(
                          devHelper.getObjectValue(product, "maxPrice"),
                        )}`
                    : component
                        .getApp()
                        .priceFormater(
                          devHelper.getObjectValue(product, "_values.price"),
                        )}
                </Typography>
              )}
              {showGems && (
                <Box>
                  {!product.simple &&
                    gems.map((gem, index) => (
                      <Box
                        key={"gem-" + slug + "-" + index}
                        sx={{
                          position: "relative",
                          cursor: "pointer",
                          width: "35px",
                          height: "35px",
                          display: "inline-block",
                          margin: "0px 3px",
                          borderRadius: "50px",
                          padding: "3px",
                          border:
                            devHelper.getObjectValue(product, "active_gem") ===
                            index
                              ? "1px solid #000"
                              : "1px solid transparent",
                        }}
                      >
                        {!component.getData(
                          `default.ready-${slug}-gem-${index}`,
                          false,
                        ) && (
                          <Skeleton
                            variant="rectangular"
                            width="100%"
                            height="100%"
                            sx={{
                              position: "absolute",
                              top: 0,
                              left: 0,
                              borderRadius: "50px",
                            }}
                          />
                        )}
                        <CardMedia
                          component="img"
                          image={
                            component.getData(
                              `default.img-error-${slug}-gem-${index}`,
                              false,
                            )
                              ? mediaUrl(filesUrl, "assets/gem-not-found.png", {
                                  width: 40,
                                  height: 40,
                                  version: imgVersion,
                                })
                              : mediaUrl(
                                  filesUrl,
                                  `gems/${devHelper.getObjectValue(
                                    gem,
                                    "item._values.slug",
                                  )}.png`,
                                  {
                                    width: 40,
                                    height: 40,
                                    version: imgVersion,
                                  },
                                )
                          }
                          title={devHelper.getObjectValue(
                            gem,
                            "item._values.name",
                          )}
                          onClick={() => {
                            changeProduct(gem, index);
                          }}
                          onLoad={() => {
                            component.setData({
                              [`default.ready-${slug}-gem-${index}`]: true,
                            });
                          }}
                          onError={() => {
                            component.setData({
                              [`default.img-error-${slug}-gem-${index}`]: true,
                            });
                          }}
                        />
                      </Box>
                    ))}
                </Box>
              )}
            </Box>
          </Fade>
        </Box>
      )}
    </Box>
  );
}
